import { useState, useEffect, useCallback } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  IdTokenResult,
} from 'firebase/auth';
import { auth } from '@/config/firebase';
import type { AdminClaims } from '@/types';

interface AuthState {
  user: User | null;
  claims: AdminClaims | null;
  loading: boolean;
  error: string | null;
}

interface UseAuthReturn extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshClaims: () => Promise<void>;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

/**
 * Authentication hook for managing Firebase Auth state
 * Provides login, logout, and admin claim verification
 */
export const useAuth = (): UseAuthReturn => {
  const [state, setState] = useState<AuthState>({
    user: null,
    claims: null,
    loading: true,
    error: null,
  });

  // Fetch custom claims from ID token
  const fetchClaims = useCallback(async (user: User): Promise<AdminClaims | null> => {
    try {
      const idTokenResult: IdTokenResult = await user.getIdTokenResult(true);
      const claims = idTokenResult.claims;

      return {
        admin: claims.admin === true,
        adminLevel: claims.adminLevel as AdminClaims['adminLevel'],
      };
    } catch (error) {
      console.error('Failed to fetch claims:', error);
      return null;
    }
  }, []);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const claims = await fetchClaims(user);
        setState({
          user,
          claims,
          loading: false,
          error: null,
        });
      } else {
        setState({
          user: null,
          claims: null,
          loading: false,
          error: null,
        });
      }
    });

    return () => unsubscribe();
  }, [fetchClaims]);

  // Sign in with email and password
  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const claims = await fetchClaims(credential.user);

      if (!claims?.admin) {
        // Sign out if not an admin
        await firebaseSignOut(auth);
        throw new Error('Access denied. You must be an admin to access this portal.');
      }

      setState({
        user: credential.user,
        claims,
        loading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to sign in. Please try again.';

      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));

      throw error;
    }
  }, [fetchClaims]);

  // Sign out
  const signOut = useCallback(async (): Promise<void> => {
    try {
      await firebaseSignOut(auth);
      setState({
        user: null,
        claims: null,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }, []);

  // Refresh claims (useful after admin grants new permissions)
  const refreshClaims = useCallback(async (): Promise<void> => {
    if (state.user) {
      const claims = await fetchClaims(state.user);
      setState((prev) => ({ ...prev, claims }));
    }
  }, [state.user, fetchClaims]);

  return {
    ...state,
    signIn,
    signOut,
    refreshClaims,
    isAdmin: state.claims?.admin === true,
    isSuperAdmin: state.claims?.admin === true && state.claims?.adminLevel === 'super',
  };
};

export default useAuth;
